import type { Key, KeyboardEvent, ReactNode, RefObject } from 'react';

import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SearchLg } from '@untitledui/icons/SearchLg';
import { XClose } from '@untitledui/icons/XClose';
import {
  isValidElement,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
} from 'react';
import { FocusScope, useFocusManager } from 'react-aria';
import {
  ButtonContext,
  ComboBox as AriaComboBox,
  Group as AriaGroup,
  Input as AriaInput,
  ListBox as AriaListBox,
  ComboBoxStateContext,
} from 'react-aria-components';

import { HintText } from '@/components/base/input/hint-text';
import { Label } from '@/components/base/input/label';
import { Popover } from '@/components/base/select/popover';
import {
  SelectContext,
  type SelectItemType,
  sizes,
} from '@/components/base/select/select';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { useThrottledValue } from '@/hooks/use-throttled-value';
import { cx } from '@/utils/cx';
import { isReactComponent } from '@/utils/is-react-component';

import { SelectItem } from './select-item';

/** Per-size classes, so adding a size variant touches exactly one place. */
const sizeStyles = {
  xs: {
    groupGap: 'gap-1.5',
    searchIcon: 'size-3.5',
    clearIcon: 'size-3.5 stroke-[2.5px]',
    emptyState: 'px-2.5 py-2 text-xs',
    tagsGap: 'gap-1',
    pill: 'py-0 text-xs',
    pillIcon: 'size-3.5',
    inputText: 'text-xs',
  },
  sm: {
    groupGap: 'gap-2',
    searchIcon: 'size-4',
    clearIcon: 'size-4 stroke-[2.5px]',
    emptyState: 'px-3 py-4 text-sm',
    tagsGap: 'gap-1.5',
    pill: 'py-0.5 text-sm',
    pillIcon: 'size-4',
    inputText: 'text-sm',
  },
  md: {
    groupGap: 'gap-2',
    searchIcon: 'size-5',
    clearIcon: 'size-4 stroke-[2.5px]',
    emptyState: 'px-3 py-4 text-sm',
    tagsGap: 'gap-1.5',
    pill: 'py-0.5 text-sm',
    pillIcon: 'size-4',
    inputText: 'text-md',
  },
} as const;

interface AsyncMultiSelectProps<T extends string = string> {
  /** Currently selected items (controlled). */
  value: SelectItemType<T>[];
  /** Called when items are added or removed. */
  onChange: (items: SelectItemType<T>[]) => void;
  /** Async search function. Receives the query string, returns matching items. */
  onSearch: (query: string) => Promise<SelectItemType<T>[]>;
  /** Placeholder text for the input. */
  placeholder?: string;
  /** Label text above the select. */
  label?: string;
  /** Hint text below the select. */
  hint?: string;
  /** Tooltip on the label. */
  tooltip?: string;
  /** Size variant. */
  size?: 'xs' | 'sm' | 'md';
  /** Maximum number of items that can be selected. */
  maxSelections?: number;
  /** Minimum characters before triggering search. */
  minSearchChars?: number;
  /** Throttle interval in ms. */
  throttleMs?: number;
  /** Whether the field is in an error state. */
  isInvalid?: boolean;
  /** Whether the field is disabled. */
  isDisabled?: boolean;
  /** Whether the field is required. */
  isRequired?: boolean;
  /** Icon rendered at the trailing end of the trigger. */
  trailingIcon?: ReactNode;
  /** Additional className for the root wrapper. */
  className?: string;
  /** Additional className for the popover dropdown. */
  popoverClassName?: string;
}

export function AsyncMultiSelect<T extends string = string>({
  value,
  onChange,
  onSearch,
  placeholder = 'Search',
  label,
  hint,
  tooltip,
  size = 'sm',
  maxSelections,
  minSearchChars = 2,
  throttleMs = 500,
  isInvalid,
  isDisabled,
  isRequired,
  trailingIcon,
  className,
  popoverClassName,
}: AsyncMultiSelectProps<T>) {
  const instanceId = useId();
  const [query, setQuery] = useState('');

  const triggerRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState('');

  const onResize = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopoverWidth(rect.width + 'px');
  }, []);

  useResizeObserver({ ref: triggerRef, onResize, box: 'border-box' });
  const throttledQuery = useThrottledValue(query, throttleMs);

  const isSearchActive = throttledQuery.length >= minSearchChars;
  const searchResultsQuery = useQuery<SelectItemType<T>[]>({
    queryKey: ['async-multi-select', instanceId, throttledQuery],
    queryFn: () => onSearch(throttledQuery),
    enabled: isSearchActive,
    placeholderData: keepPreviousData,
  });

  const searchUiState =
    query.length < minSearchChars
      ? 'below-min'
      : query !== throttledQuery || searchResultsQuery.isFetching
        ? 'waiting'
        : searchResultsQuery.status === 'error'
          ? 'error'
          : 'ready';

  // Keep stale results hidden until the throttled query catches up and the
  // matching response settles.
  const results =
    searchUiState === 'ready' ? (searchResultsQuery.data ?? []) : [];

  // Derived state
  const selectedIds = new Set(value.map((v) => v.id));
  const atMax =
    typeof maxSelections === 'number' && value.length >= maxSelections;

  const filteredResults = atMax
    ? []
    : results.filter((r) => !selectedIds.has(r.id));

  const handleSelect = (id: Key | null) => {
    if (!id) return;
    if (atMax) return;

    const item = results.find((r) => r.id === id);
    if (!item) return;

    if (!selectedIds.has(id as T)) {
      onChange([...value, item]);
    }

    setQuery('');
  };

  const getEmptyStateMessage = () => {
    if (atMax) {
      return `Maximum ${maxSelections} items selected`;
    }

    switch (searchUiState) {
      case 'below-min':
        return `Type at least ${minSearchChars} characters to start searching`;
      case 'waiting':
        return t`Searching...`;
      case 'error':
        return t`Something went wrong`;
      default:
        return t`No results found`;
    }
  };

  return (
    <SelectContext.Provider value={{ size }}>
      <AriaComboBox
        allowsEmptyCollection
        menuTrigger="focus"
        items={filteredResults}
        inputValue={query}
        onInputChange={setQuery}
        selectedKey={null}
        onSelectionChange={handleSelect}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        isRequired={isRequired}
        aria-label={label ? undefined : placeholder}
        className={className}
      >
        {(state) => (
          <div className="flex flex-col gap-1.5">
            {label && (
              <Label isRequired={state.isRequired} tooltip={tooltip}>
                {label}
              </Label>
            )}

            <AriaGroup
              ref={triggerRef}
              className={({ isFocusWithin, isDisabled: groupDisabled }) =>
                cx(
                  'flex w-full items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset',
                  sizeStyles[size].groupGap,
                  groupDisabled && 'cursor-not-allowed bg-disabled_subtle',
                  isFocusWithin && 'ring-2 ring-brand',
                  isInvalid && !isFocusWithin && 'ring-error_subtle',
                  isInvalid && isFocusWithin && 'ring-2 ring-error',
                  sizes[size].root,
                )
              }
              onFocus={onResize}
              onPointerEnter={onResize}
            >
              {({ isDisabled: groupDisabled }) => (
                <>
                  <SearchLg
                    className={cx(
                      'pointer-events-none shrink-0 text-fg-quaternary',
                      sizeStyles[size].searchIcon,
                    )}
                  />
                  <FocusScope
                    contain={false}
                    // oxlint-disable-next-line jsx-a11y/no-autofocus -- upstream lib code, autoFocus=false is explicitly disabling
                    autoFocus={false}
                    restoreFocus={false}
                  >
                    <TagsAndInput
                      items={value}
                      onRemove={(id: string) =>
                        onChange(value.filter((item) => item.id !== id))
                      }
                      isDisabled={groupDisabled}
                      placeholder={placeholder}
                      size={size}
                    />
                  </FocusScope>
                  {value.length > 0 && !groupDisabled && (
                    <button
                      type="button"
                      className="flex shrink-0 cursor-pointer items-center rounded-sm p-0.5 text-fg-quaternary transition hover:bg-primary_hover hover:text-fg-quaternary_hover"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange([]);
                        setQuery('');
                      }}
                      aria-label="Clear all selections"
                    >
                      <XClose className={sizeStyles[size].clearIcon} />
                    </button>
                  )}
                  {trailingIcon && (
                    <ButtonContext.Provider value={{}}>
                      <div className="flex shrink-0 items-center">
                        {trailingIcon}
                      </div>
                    </ButtonContext.Provider>
                  )}
                </>
              )}
            </AriaGroup>

            <Popover
              size={size}
              triggerRef={triggerRef as RefObject<HTMLElement>}
              style={{ width: popoverWidth }}
              className={popoverClassName}
            >
              <AriaListBox
                selectionMode="multiple"
                className="size-full outline-hidden"
                renderEmptyState={() => (
                  <div
                    className={cx(
                      'text-center text-tertiary',
                      sizeStyles[size].emptyState,
                    )}
                  >
                    {getEmptyStateMessage()}
                  </div>
                )}
              >
                {filteredResults.map((item) => (
                  <SelectItem key={item.id} {...item} />
                ))}
              </AriaListBox>

              {query === '' && filteredResults.length > 0 && (
                <div className="border-t border-secondary px-3 py-2 text-xs text-tertiary italic">
                  <Trans>Type to search for more options</Trans>
                </div>
              )}
            </Popover>

            {hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
          </div>
        )}
      </AriaComboBox>
    </SelectContext.Provider>
  );
}

// ── Internal: Tags + Input ──────────────────────────────────────────────────

interface TagsAndInputProps {
  items: SelectItemType[];
  onRemove: (id: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  size: 'xs' | 'sm' | 'md';
}

function TagsAndInput({
  items,
  onRemove,
  isDisabled,
  placeholder,
  size,
}: TagsAndInputProps) {
  const focusManager = useFocusManager();
  const comboBoxState = useContext(ComboBoxStateContext);

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const isCaretAtStart =
      event.currentTarget.selectionStart === 0 &&
      event.currentTarget.selectionEnd === 0;

    if (!isCaretAtStart && event.currentTarget.value !== '') return;

    switch (event.key) {
      case 'Backspace':
      case 'ArrowLeft':
        focusManager?.focusPrevious({ wrap: false, tabbable: false });
        break;
      case 'ArrowRight':
        focusManager?.focusNext({ wrap: false, tabbable: false });
        break;
    }
  };

  const handleInputMouseDown = () => {
    if (comboBoxState && !comboBoxState.isOpen) {
      comboBoxState.open();
    }
  };

  const handleTagKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    id: string,
  ) => {
    if (event.key === 'Tab') return;

    event.preventDefault();

    const isFirstTag = items[0]?.id === id;

    switch (event.key) {
      case ' ':
      case 'Enter':
      case 'Backspace':
        if (isFirstTag) {
          focusManager?.focusNext({ wrap: false, tabbable: false });
        } else {
          focusManager?.focusPrevious({ wrap: false, tabbable: false });
        }
        onRemove(id);
        break;
      case 'ArrowLeft':
        focusManager?.focusPrevious({ wrap: false, tabbable: false });
        break;
      case 'ArrowRight':
        focusManager?.focusNext({ wrap: false, tabbable: false });
        break;
      case 'Escape':
        comboBoxState?.close();
        break;
    }
  };

  const isEmpty = items.length === 0;
  const styles = sizeStyles[size];

  return (
    <div
      className={cx(
        'relative flex w-full flex-1 flex-row flex-wrap items-center justify-start',
        styles.tagsGap,
      )}
    >
      {!isEmpty &&
        items.map((item) => {
          const Icon = item.icon;
          return (
            <span
              key={item.id}
              className={cx(
                'flex shrink-0 items-center gap-1 rounded-md border border-primary bg-brand-primary pr-1 pl-1.5 text-secondary',
                styles.pill,
              )}
            >
              {isReactComponent(Icon) ? (
                <Icon
                  className={cx('shrink-0', styles.pillIcon)}
                  aria-hidden="true"
                />
              ) : isValidElement(Icon) ? (
                Icon
              ) : null}
              <p className="max-w-40 truncate whitespace-nowrap select-none">
                {item.label}
              </p>
              <button
                type="button"
                disabled={isDisabled}
                className="flex cursor-pointer items-center rounded-sm p-0.5 text-fg-quaternary transition hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed"
                onKeyDown={(event) => handleTagKeyDown(event, item.id)}
                onClick={() => onRemove(item.id)}
                aria-label={`Remove ${item.label}`}
              >
                <XClose className="size-3 stroke-[3px]" />
              </button>
            </span>
          );
        })}

      <div
        className={cx(
          'relative flex flex-1 flex-row items-center',
          !isEmpty && 'ml-0.5',
        )}
      >
        <AriaInput
          placeholder={isEmpty ? placeholder : undefined}
          onKeyDown={handleInputKeyDown}
          onMouseDown={handleInputMouseDown}
          className={cx(
            'w-full flex-[1_0_0] appearance-none bg-transparent text-primary caret-alpha-black/90 outline-none placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed disabled:text-disabled disabled:placeholder:text-disabled',
            styles.inputText,
          )}
        />
      </div>
    </div>
  );
}
